import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import Header from '../../components/Header';
import LoadingSpinner from '../../components/LoadingSpinner';
import { tokens } from '../../theme';
import {
  fetchPlxSourceGroupingStats,
  fetchPlxBlogGroupingStats,
  fetchPlxGroupingOrders,
  fetchPlxSourceGroupingExport,
  fetchPlxBlogGroupingExport,
  PlxGroupingStats,
  PlxGroupingOrder,
} from '../../helpers/plxOrdersData';
import { exportToExcel } from '../../helpers/exportFuncs';
import moment from 'moment';

const PlxGroupingsPage: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [sourceGroupings, setSourceGroupings] = useState<PlxGroupingStats[]>([]);
  const [blogGroupings, setBlogGroupings] = useState<PlxGroupingStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportingSource, setExportingSource] = useState(false);
  const [exportingBlog, setExportingBlog] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'source' | 'blog'>('source');
  const [modalCategory, setModalCategory] = useState<string>('');
  const [modalOrders, setModalOrders] = useState<PlxGroupingOrder[]>([]);
  const [modalPage, setModalPage] = useState(0);
  const [modalRowsPerPage, setModalRowsPerPage] = useState(50);
  const [modalTotal, setModalTotal] = useState(0);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [sourceData, blogData] = await Promise.all([
          fetchPlxSourceGroupingStats(),
          fetchPlxBlogGroupingStats(),
        ]);
        setSourceGroupings(sourceData);
        setBlogGroupings(blogData);
      } catch (error) {
        console.error('Error loading grouping stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleViewOrders = async (type: 'source' | 'blog', category: string) => {
    setModalType(type);
    setModalCategory(category);
    setModalOpen(true);
    setModalPage(0);
    await loadModalOrders(type, category, 1);
  };

  const loadModalOrders = async (type: 'source' | 'blog', category: string, page: number) => {
    setModalLoading(true);
    try {
      const response = await fetchPlxGroupingOrders(type, category, page, modalRowsPerPage);
      setModalOrders(response.data);
      setModalTotal(response.pagination.total);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleModalChangePage = async (event: unknown, newPage: number) => {
    setModalPage(newPage);
    await loadModalOrders(modalType, modalCategory, newPage + 1);
  };

  const handleModalChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setModalRowsPerPage(newRowsPerPage);
    setModalPage(0);
    await loadModalOrders(modalType, modalCategory, 1);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalOrders([]);
    setModalTotal(0);
  };

  const handleSourceExport = async () => {
    try {
      setExportingSource(true);
      const data = await fetchPlxSourceGroupingExport();

      const exportData = data.map((order) => ({
        Kategorie: order.category,
        Bestellnummer: order.ordernumber,
        Bestelldatum: moment.utc(order.created_on).format('YYYY-MM-DD HH:mm:ss'),
        Banner: order.banner,
        'Offer URL': order.offerurl,
        'E-Mail': order.email,
        Produkt: order.product_name,
      }));

      const fileName = `plx-source-groupings-${moment().format('YYYY-MM-DD')}`;
      exportToExcel(exportData, fileName);
    } catch (error) {
      console.error('Error exporting source groupings:', error);
      alert('Fehler beim Exportieren der Quellen-Gruppierungen');
    } finally {
      setExportingSource(false);
    }
  };

  const handleBlogExport = async () => {
    try {
      setExportingBlog(true);
      const data = await fetchPlxBlogGroupingExport();

      const exportData = data.map((order) => ({
        Kategorie: order.category,
        Bestellnummer: order.ordernumber,
        Bestelldatum: moment.utc(order.created_on).format('YYYY-MM-DD HH:mm:ss'),
        Banner: order.banner,
        'Offer URL': order.offerurl,
        'E-Mail': order.email,
        Produkt: order.product_name,
      }));

      const fileName = `plx-blog-groupings-${moment().format('YYYY-MM-DD')}`;
      exportToExcel(exportData, fileName);
    } catch (error) {
      console.error('Error exporting blog groupings:', error);
      alert('Fehler beim Exportieren der Blog-Gruppierungen');
    } finally {
      setExportingBlog(false);
    }
  };

  const renderGroupingCard = (grouping: PlxGroupingStats, type: 'source' | 'blog') => (
    <Accordion
      key={grouping.category}
      sx={{
        backgroundColor: colors.primary[400],
        mb: 2,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" fontWeight="600">
            {grouping.category}
          </Typography>
          <Chip
            label={`${grouping.order_count.toLocaleString('de-DE')} Bestellungen`}
            color="primary"
            size="small"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          {/* Rules Section */}
          <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
            <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Regeln
          </Typography>
          <Box sx={{ mb: 3 }}>
            {grouping.patterns.map((pattern, idx) => (
              <Chip
                key={idx}
                label={pattern}
                variant="outlined"
                sx={{
                  mr: 1,
                  mb: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                }}
              />
            ))}
          </Box>

          {/* Examples Section */}
          <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
            Beispiele ({grouping.examples.length})
          </Typography>
          <Box sx={{ mb: 2 }}>
            {grouping.examples.map((example, idx) => (
              <Typography
                key={idx}
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  backgroundColor: theme.palette.mode === 'dark' ? colors.primary[500] : '#f5f5f5',
                  color: theme.palette.mode === 'dark' ? colors.grey[100] : '#333',
                  p: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  wordBreak: 'break-all',
                }}
              >
                {example || '(leer)'}
              </Typography>
            ))}
          </Box>

          {/* View All Button */}
          <Button
            variant="contained"
            startIcon={<VisibilityIcon />}
            onClick={() => handleViewOrders(type, grouping.category)}
          >
            Alle Bestellungen anzeigen
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box m="20px">
      <Header
        title="PLX Gruppierungsregeln"
        subtitle="Übersicht der Kategorisierungsregeln für Quellen und Blogs"
      />

      {/* Source Groupings */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h3" fontWeight="600" sx={{ color: colors.grey[100] }}>
            Quellen-Gruppierung
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleSourceExport}
            disabled={exportingSource}
            sx={{
              backgroundColor: colors.blueAccent[700],
              '&:hover': {
                backgroundColor: colors.blueAccent[800],
              },
            }}
          >
            {exportingSource ? 'Exportiere...' : 'Export als Excel'}
          </Button>
        </Box>
        <Typography variant="body1" sx={{ mb: 2, color: colors.grey[300] }}>
          Kategorisierung basierend auf der Offer URL (o.offerurl)
        </Typography>
        {sourceGroupings.map((grouping) => renderGroupingCard(grouping, 'source'))}
      </Box>

      {/* Blog Groupings */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h3" fontWeight="600" sx={{ color: colors.grey[100] }}>
            Blog-Gruppierung
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleBlogExport}
            disabled={exportingBlog}
            sx={{
              backgroundColor: colors.blueAccent[700],
              '&:hover': {
                backgroundColor: colors.blueAccent[800],
              },
            }}
          >
            {exportingBlog ? 'Exportiere...' : 'Export als Excel'}
          </Button>
        </Box>
        <Typography variant="body1" sx={{ mb: 2, color: colors.grey[300] }}>
          Kategorisierung basierend auf dem Banner (o.banner)
        </Typography>
        {blogGroupings.map((grouping) => renderGroupingCard(grouping, 'blog'))}
      </Box>

      {/* Orders Detail Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          },
        }}
      >
        <DialogTitle>
          Bestellungen - {modalCategory} ({modalType === 'source' ? 'Quelle' : 'Blog'})
        </DialogTitle>
        <DialogContent>
          {modalLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Bestellnummer</TableCell>
                      <TableCell>Bestelldatum</TableCell>
                      <TableCell>Banner</TableCell>
                      <TableCell>Offer URL</TableCell>
                      <TableCell>E-Mail</TableCell>
                      <TableCell>Produkt</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modalOrders.map((order) => (
                      <TableRow key={order.ordernumber}>
                        <TableCell>{order.ordernumber}</TableCell>
                        <TableCell>
                          {new Date(order.created_on).toLocaleDateString('de-DE')}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            wordBreak: 'break-all',
                            maxWidth: 200,
                          }}
                        >
                          {order.banner || '(leer)'}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            wordBreak: 'break-all',
                            maxWidth: 300,
                          }}
                        >
                          {order.offerurl || '(leer)'}
                        </TableCell>
                        <TableCell>{order.email}</TableCell>
                        <TableCell>{order.product_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={modalTotal}
                rowsPerPage={modalRowsPerPage}
                page={modalPage}
                onPageChange={handleModalChangePage}
                onRowsPerPageChange={handleModalChangeRowsPerPage}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlxGroupingsPage;
